"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import Image from "next/image";
import moment from "moment";
import { Check, Info, X } from "lucide-react";
import { useTranslation } from "../hooks/useTranslation";
import type { Locale } from "../providers/LanguageProvider";
import { Spinner } from "./loading-spinner";

const figmaChevronIcon = "/figma-assets/547ebfe389d562e21df6e4b678ba83a357d28d24.svg";
const TEAM_FLAG_PLACEHOLDER =
  "https://ocnc-fantasy.s3.eu-west-1.amazonaws.com/flags/flag-placeholder.jpg";

type Outcome = "KO/TKO" | "Decision" | string;

type FighterLite = {
  id: string;
  short: string;
  name?: string;
};

export type MatchData = {
  id: number;
  date?: string;
  home: FighterLite;
  away: FighterLite;
  competitionFeed?: string;
  homeScore?: number;
  awayScore?: number;
  final?: number;
};

export type PickValue = {
  winnerId: number | string | null;
  outcome: string | null;
  round: number | null;
  booster: boolean;
  homeGoals?: number | null;
  awayGoals?: number | null;
};

type Odds = { home: number; draw: number; away: number };
type TeamRecentMatch = {
  id: number;
  date?: string;
  competitionFeed?: string;
  home?: FighterLite;
  away?: FighterLite;
  homeScore?: number;
  awayScore?: number;
};

type SaveStatus = "idle" | "pending" | "saved";

type MatchCardProps = {
  match: MatchData;
  pick: PickValue;
  /**
   * Called on every pick mutation. The optional `meta.side` tells the parent
   * which score input was just edited so it can scope follow-up focus moves
   * (e.g. "advance to next card only when away was the latest edit").
   */
  onPickChange: (pick: PickValue, meta?: { side?: "home" | "away" }) => void;
  disabled?: boolean;
  isSaving?: boolean;
  /** Drives the inline checkmark indicator between score inputs. */
  saveStatus?: SaveStatus;
  boosterDisabled?: boolean;
  boosterAlreadyUsed?: boolean; // True if booster was used on a past match
  isFavorite?: boolean;
  odds: Odds;
  resolvedScore?: string;
  onAuthRequired?: () => void; // Called when user tries to pick but needs authentication
  seasonId?: string | number;
};

// Competition name mapping
const COMPETITION_NAMES: Record<string, string> = {
  BE1: "Pro League",
  CFL: "Conference League",
  CL: "Champions League",
  DE1: "Bundesliga",
  EL: "Europa League",
  ES1: "La Liga",
  FR1: "Ligue 1",
  GB1: "Premier League",
  IT1: "Serie A",
  TR1: "Süper Lig",
};

// Module-scope reference for "no pick yet" so the prop kept stable across renders
// in consumers (e.g. Predictions) and React.memo's shallow compare can skip
// re-rendering cards whose pick hasn't actually changed.
export const EMPTY_PICK: PickValue = Object.freeze({
  winnerId: null,
  outcome: null,
  round: null,
  booster: false,
  homeGoals: null,
  awayGoals: null,
}) as PickValue;

// Hoisted out of MatchCard to keep a stable component identity across renders.
// When this was declared inside MatchCard, every render produced a new function
// reference, so React's reconciler unmounted and re-mounted the entire subtree
// (including the flag <img> elements) on every keystroke — the visible "flag
// flash" reported on the Predictions page.
type MatchCardContainerProps = {
  isFavorite: boolean;
  yourTeamLabel: string;
  children: React.ReactNode;
};

const MatchCardContainer: React.FC<MatchCardContainerProps> = ({
  isFavorite,
  yourTeamLabel,
  children,
}) => {
  if (isFavorite) {
    return (
      <div className="relative rounded-2xl px-4 py-4 flex flex-col items-center justify-center gap-2 w-full h-full overflow-hidden bg-[var(--bet-red)] bg-[url('/assets/08ad33feace27a993b49a609da61c5e4962b1a0d.png')] bg-cover bg-center">
        <div className="flex flex-col font-['PanamaBold'] h-6 justify-center leading-[0] text-[16px] text-white text-center w-full relative z-10 flex-shrink-0">
          <p className="leading-[18px]">{yourTeamLabel}</p>
        </div>
        {children}
      </div>
    );
  }
  return (
    <div className="w-full h-full overflow-hidden rounded-[8px] bg-[var(--rtbf-blue-dark,#003E63)] shadow-[0px_5px_15px_0px_rgba(0,0,0,0.10)] flex flex-col">
      {children}
    </div>
  );
};

type MatchCardHeaderProps = {
  dateText: string;
  showFinalScore: boolean;
  scoreText: string | null;
  useDesktopCardLayout: boolean;
  isFinalState: boolean;
  showLiveScore: boolean;
  isDeadlinePassed: boolean;
};

const HEADER_BASE_TEXT_CLASS =
  "font-medium text-[14px] text-center whitespace-pre text-[#C4D1EA]";

const MatchCardHeader: React.FC<MatchCardHeaderProps> = ({
  dateText,
  showFinalScore,
  scoreText,
  useDesktopCardLayout,
  isFinalState,
  showLiveScore,
  isDeadlinePassed,
}) => {
  if (showFinalScore && scoreText) {
    return (
      <div className="flex items-center justify-center">
        <p
          className={`font-normal text-center whitespace-pre text-[#C4D1EA] ${useDesktopCardLayout ? "text-[14px]" : "text-[12px]"}`}
        >
          {`Fin: ${scoreText}`}
        </p>
      </div>
    );
  }

  if (isFinalState) {
    return (
      <div className="flex items-center justify-center">
        <p className={HEADER_BASE_TEXT_CLASS}>Fin</p>
      </div>
    );
  }

  if (showLiveScore && scoreText) {
    return (
      <div className="flex items-center justify-center">
        <p className={HEADER_BASE_TEXT_CLASS}>{`LIVE: ${scoreText}`}</p>
      </div>
    );
  }

  if (isDeadlinePassed && !isFinalState) {
    return (
      <div className="flex items-center justify-center">
        <p className={HEADER_BASE_TEXT_CLASS}>LIVE</p>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center">
      <p className="font-normal text-[14px] text-center whitespace-pre text-[#C4D1EA]">
        {dateText}
      </p>
    </div>
  );
};

function MatchCard({
  match,
  pick,
  onPickChange,
  disabled = false,
  isSaving = false,
  saveStatus = "idle",
  boosterDisabled = false,
  boosterAlreadyUsed = false,
  isFavorite = false,
  odds = { home: 0, draw: 0, away: 0 },
  resolvedScore,
  onAuthRequired,
  seasonId,
}: MatchCardProps) {
  const { t, locale } = useTranslation();
  const [hoveredScoreInput, setHoveredScoreInput] = React.useState<"home" | "away" | null>(null);
  const alwaysRevealMatches = true;
  const [imgError, setImgError] = React.useState<{ home: boolean; away: boolean }>({ home: false, away: false });
  const [isDetailsOpen, setIsDetailsOpen] = React.useState(false);
  const [isInfoOpen, setIsInfoOpen] = React.useState(false);
  const [activeInfoTab, setActiveInfoTab] = React.useState<"home" | "away">("home");
  const [isClient, setIsClient] = React.useState(false);
  const [cardWidth, setCardWidth] = React.useState(0);
  const cardRef = React.useRef<HTMLDivElement | null>(null);
  const homeInputRef = React.useRef<HTMLInputElement | null>(null);
  const awayInputRef = React.useRef<HTMLInputElement | null>(null);
  const scoreColumnRef = React.useRef<HTMLDivElement | null>(null);
  const authTooltipRef = React.useRef<HTMLDivElement | null>(null);
  // Position for the "sign in to play" tooltip. It is portaled to <body> so the
  // card's `overflow-hidden` (and the viewport edge on mobile) can't clip the
  // longer localized labels (e.g. FR "Se connecter pour jouer").
  const [authTooltipPos, setAuthTooltipPos] = React.useState<
    { left: number; top: number; arrowOffset: number } | null
  >(null);
  const getTeamFlagSrc = (teamId: string) =>
    teamId && String(teamId).trim() !== ""
      ? `https://ocnc-fantasy.s3.eu-west-1.amazonaws.com/flags/${teamId}.jpg`
      : TEAM_FLAG_PLACEHOLDER;
  const teamTranslationKey = React.useCallback((name: string) => {
    return name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "");
  }, []);
  const translateTeamName = React.useCallback(
    (name?: string) => {
      if (!name) return "";
      const key = teamTranslationKey(name);
      if (!key) return name;
      const translated = t(`teams.${key}`);
      return translated === `teams.${key}` ? name : translated;
    },
    [t, teamTranslationKey]
  );
  const hasExactScorePick =
    typeof pick.homeGoals === "number" && typeof pick.awayGoals === "number";
  const mockedNow = React.useMemo(() => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const mockNowParam = params.get("mockNow");
    if (!mockNowParam) return null;
    const normalizedMockNow = mockNowParam.includes(" ") && !mockNowParam.includes("+")
      ? mockNowParam.replace(" ", "+")
      : mockNowParam;
    const parsed = moment(normalizedMockNow);
    return parsed.isValid() ? parsed : null;
  }, []);
  const getNow = React.useCallback(
    () => (mockedNow ? mockedNow.clone() : moment()),
    [mockedNow]
  );
  const predictedWinnerId = React.useMemo(() => {
    if (!hasExactScorePick) return pick.winnerId;
    if ((pick.homeGoals ?? 0) > (pick.awayGoals ?? 0)) return match.home.id;
    if ((pick.awayGoals ?? 0) > (pick.homeGoals ?? 0)) return match.away.id;
    return "draw";
  }, [hasExactScorePick, pick.winnerId, pick.homeGoals, pick.awayGoals, match.home.id, match.away.id]);
  const scoreText =
    typeof match.homeScore === "number" && typeof match.awayScore === "number"
      ? `${match.homeScore} - ${match.awayScore}`
      : null;
  const isResultFinal = match.final === 1;

  // Get competition name
  const competitionName = match.competitionFeed
    ? COMPETITION_NAMES[match.competitionFeed] || match.competitionFeed
    : null;

  // Format match date - use locale-aware calendar formatting
  // Helper function to get locale-aware relative time string
  const getLocaleRelativeTime = React.useCallback((key: 'sameDay' | 'nextDay' | 'lastDay'): string => {
    // Use locale-specific strings based on the current locale
    const localeStrings: Record<Locale, Record<string, string>> = {
      nl: {
        sameDay: 'Vandaag',
        nextDay: 'Morgen',
        lastDay: 'Gisteren',
      },
      en: {
        sameDay: 'Today',
        nextDay: 'Tomorrow',
        lastDay: 'Yesterday',
      },
      fr: {
        sameDay: "Aujourd'hui",
        nextDay: 'Demain',
        lastDay: 'Hier',
      },
      tr: {
        sameDay: 'Bugün',
        nextDay: 'Yarın',
        lastDay: 'Dün',
      },
    };
    
    return localeStrings[locale]?.[key] || localeStrings.en[key] || '';
  }, [locale]);

  const formattedDate = React.useMemo(() => {
    if (!match.date) return "TBD";
    const momentInstance = moment.utc(match.date).local();
    return momentInstance.calendar(null, {
      sameDay: `[${getLocaleRelativeTime('sameDay')}] DD.MM - HH:mm`,
      nextDay: `[${getLocaleRelativeTime('nextDay')}] DD.MM - HH:mm`,
      lastDay: `[${getLocaleRelativeTime('lastDay')}] DD.MM - HH:mm`,
      nextWeek: "ddd DD.MM - HH:mm",
      lastWeek: "ddd DD.MM - HH:mm",
      sameElse: "ddd DD.MM - HH:mm",
    });
  }, [match.date, locale, getLocaleRelativeTime]);

  const shortFormattedDate = React.useMemo(() => {
    if (!match.date) return "TBD";
    const momentInstance = moment.utc(match.date).local();
    return momentInstance.calendar(null, {
      sameDay: `[${getLocaleRelativeTime('sameDay')}] DD.MM`,
      nextDay: `[${getLocaleRelativeTime('nextDay')}] DD.MM`,
      lastDay: `[${getLocaleRelativeTime('lastDay')}] DD.MM`,
      nextWeek: "ddd DD.MM",
      lastWeek: "ddd DD.MM",
      sameElse: "ddd DD.MM",
    });
  }, [match.date, locale, getLocaleRelativeTime]);

  // Calculate if match should be revealed
  const [hoursUntilReveal, setHoursUntilReveal] = React.useState<number | null>(null);
  const [isRevealed, setIsRevealed] = React.useState(true);

  React.useEffect(() => {
    if (alwaysRevealMatches) {
      setIsRevealed(true);
      setHoursUntilReveal(null);
      return;
    }

    if (!match.date) {
      setIsRevealed(true);
      return;
    }

    const updateRevealStatus = () => {
      const matchDate = moment.utc(match.date).local();
      const now = getNow();
      const hoursUntil = matchDate.diff(now, 'hours', true);
      
      // Reveal matches 48 hours before (day after tomorrow threshold)
      if (hoursUntil > 48) {
        setIsRevealed(false);
        setHoursUntilReveal(Math.ceil(hoursUntil - 48));
      } else {
        setIsRevealed(true);
        setHoursUntilReveal(null);
      }
    };

    updateRevealStatus();
    const interval = setInterval(updateRevealStatus, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [match.date, alwaysRevealMatches, getNow]);

  // Check if deadline has passed
  const isDeadlinePassed = React.useMemo(() => {
    if (!match.date) return false;
    const matchDate = moment.utc(match.date).local();
    const now = getNow();
    return now.isAfter(matchDate);
  }, [match.date, getNow]);
  const shouldAssumeFinal = React.useMemo(() => {
    if (!match.date) return false;
    if (isResultFinal) return false;
    const matchDate = moment.utc(match.date).local();
    if (!matchDate.isValid()) return false;
    const now = getNow();
    // Fallback: treat older started matches as finished to avoid stale LIVE 0-0 cards.
    return now.diff(matchDate, "hours") >= 6;
  }, [match.date, isResultFinal, getNow]);
  const isFinalState = isResultFinal || shouldAssumeFinal;
  const showLiveScore = isDeadlinePassed && !isFinalState && !!scoreText;
  const showFinalScore = isDeadlinePassed && isFinalState && !!scoreText;
  const isPastNoPrediction = isFinalState && !hasExactScorePick;
  const actualWinnerId = React.useMemo(() => {
    if (!isResultFinal) return null;
    if (typeof match.homeScore !== "number" || typeof match.awayScore !== "number") {
      return null;
    }
    if (match.homeScore > match.awayScore) {
      return match.home.id;
    }
    if (match.awayScore > match.homeScore) {
      return match.away.id;
    }
    return "draw";
  }, [isResultFinal, match.homeScore, match.awayScore, match.home.id, match.away.id]);
  const isPickCorrect = actualWinnerId !== null && predictedWinnerId === actualWinnerId;
  const isPickWrong =
    actualWinnerId !== null && predictedWinnerId !== null && predictedWinnerId !== actualWinnerId;

  // Combined disabled state
  const isButtonDisabled = disabled || isSaving || isDeadlinePassed;

  const handleScoreInput = (side: "home" | "away", value: string) => {
    // If user is not authenticated and onAuthRequired is provided, trigger auth flow
    if (onAuthRequired) {
      onAuthRequired();
      return;
    }
    const trimmed = value.trim();
    const parsedValue =
      trimmed === ""
        ? null
        : Number.isNaN(Number(trimmed))
          ? null
          : Math.max(0, Math.min(99, Number(trimmed)));
    const nextHomeGoals = side === "home" ? parsedValue : pick.homeGoals ?? null;
    const nextAwayGoals = side === "away" ? parsedValue : pick.awayGoals ?? null;
    const hasBoth = typeof nextHomeGoals === "number" && typeof nextAwayGoals === "number";
    const nextWinnerId = hasBoth
      ? nextHomeGoals > nextAwayGoals
        ? match.home.id
        : nextAwayGoals > nextHomeGoals
          ? match.away.id
          : "draw"
      : null;
    onPickChange(
      {
        ...pick,
        winnerId: nextWinnerId,
        outcome: hasBoth ? `${nextHomeGoals}-${nextAwayGoals}` : null,
        round: hasBoth ? 1 : null,
        homeGoals: nextHomeGoals,
        awayGoals: nextAwayGoals,
        booster: false,
      },
      { side }
    );
  };

  const [recentByTeam, setRecentByTeam] = React.useState<{
    home: TeamRecentMatch[];
    away: TeamRecentMatch[];
  }>({ home: [], away: [] });
  const [isLoadingRecentMatches, setIsLoadingRecentMatches] = React.useState(false);
  const hasFetchedRecentMatchesRef = React.useRef(false);

  React.useEffect(() => {
    hasFetchedRecentMatchesRef.current = false;
    setRecentByTeam({ home: [], away: [] });
  }, [match.home.id, match.away.id]);

  React.useEffect(() => {
    if (!isInfoOpen) return;
    if (hasFetchedRecentMatchesRef.current) return;

    const homeId = match.home.id;
    const awayId = match.away.id;
    if (!homeId || !awayId) {
      setRecentByTeam({ home: [], away: [] });
      return;
    }

    const controller = new AbortController();

    type ApiRecentClub = {
      id: number | null;
      name: string | null;
      short: string | null;
    };
    type ApiRecentMatch = {
      id: number;
      date: string;
      competitionFeed: string | null;
      homeScore: number;
      awayScore: number;
      final: number | null;
      home: ApiRecentClub | null;
      away: ApiRecentClub | null;
    };
    type ApiResponse = { home: ApiRecentMatch[]; away: ApiRecentMatch[] };

    const mapClub = (club: ApiRecentClub | null | undefined): FighterLite | undefined => {
      if (!club || club.id == null) return undefined;
      return {
        id: String(club.id),
        short: club.short ?? "",
        name: translateTeamName(club.name || club.short || ""),
      };
    };

    const mapMatch = (apiMatch: ApiRecentMatch): TeamRecentMatch => ({
      id: apiMatch.id,
      date: apiMatch.date,
      competitionFeed: apiMatch.competitionFeed ?? undefined,
      home: mapClub(apiMatch.home),
      away: mapClub(apiMatch.away),
      homeScore: apiMatch.homeScore,
      awayScore: apiMatch.awayScore,
    });

    const params = new URLSearchParams({
      homeClubId: String(homeId),
      awayClubId: String(awayId),
    });

    setIsLoadingRecentMatches(true);
    fetch(`/api/matches/recent?${params.toString()}`, {
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) throw new Error(`Failed (${response.status})`);
        return (await response.json()) as ApiResponse;
      })
      .then((data) => {
        setRecentByTeam({
          home: (data.home || []).map(mapMatch),
          away: (data.away || []).map(mapMatch),
        });
        hasFetchedRecentMatchesRef.current = true;
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        console.error("[MatchCard] Failed to fetch recent matches:", err);
        setRecentByTeam({ home: [], away: [] });
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsLoadingRecentMatches(false);
        }
      });

    return () => controller.abort();
  }, [isInfoOpen, match.home.id, match.away.id, translateTeamName]);

  React.useEffect(() => {
    setIsClient(true);
  }, []);

  React.useEffect(() => {
    const node = cardRef.current;
    if (!node) return;

    const updateWidth = () => {
      const rect = node.getBoundingClientRect();
      setCardWidth(rect.width);
    };

    // Initial measurement on mount.
    updateWidth();

    let observer: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) return;
        setCardWidth(entry.contentRect.width);
      });
      observer.observe(node);
    }

    // Fallback for layout changes not always caught immediately.
    window.addEventListener("resize", updateWidth);

    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, []);

  // Automatically switch layout by available card width.
  const useDesktopCardLayout = cardWidth >= 420;

  React.useLayoutEffect(() => {
    if (!onAuthRequired || hoveredScoreInput === null) {
      setAuthTooltipPos(null);
      return;
    }
    const anchor = scoreColumnRef.current;
    if (!anchor) return;

    const update = () => {
      const rect = anchor.getBoundingClientRect();
      const margin = 8;
      const anchorCenter = rect.left + rect.width / 2;
      const tooltipWidth = authTooltipRef.current?.offsetWidth ?? 0;
      let left = anchorCenter;
      if (tooltipWidth > 0) {
        const half = tooltipWidth / 2;
        const min = margin + half;
        const max = window.innerWidth - margin - half;
        // When the tooltip is wider than the viewport, fall back to centering.
        left = min <= max ? Math.min(Math.max(anchorCenter, min), max) : window.innerWidth / 2;
      }
      setAuthTooltipPos({ left, top: rect.bottom + 4, arrowOffset: anchorCenter - left });
    };

    update();
    // Re-measure after first paint so the now-rendered tooltip width can be
    // used to clamp it within the viewport.
    const raf = requestAnimationFrame(update);
    window.addEventListener("resize", update);
    window.addEventListener("scroll", update, true);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("resize", update);
      window.removeEventListener("scroll", update, true);
    };
  }, [hoveredScoreInput, onAuthRequired]);

  React.useEffect(() => {
    if (isInfoOpen) {
      setActiveInfoTab("home");
    }
  }, [isInfoOpen]);

  React.useEffect(() => {
    if (!isInfoOpen) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsInfoOpen(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isInfoOpen]);

  const getTeamResultBadge = React.useCallback(
    (teamId: string, recentMatch: TeamRecentMatch): "W" | "L" | "D" | null => {
      if (
        typeof recentMatch.homeScore !== "number" ||
        typeof recentMatch.awayScore !== "number"
      ) {
        return null;
      }
      const isTeamHome = String(recentMatch.home?.id) === teamId;
      const teamScore = isTeamHome ? recentMatch.homeScore : recentMatch.awayScore;
      const opponentScore = isTeamHome ? recentMatch.awayScore : recentMatch.homeScore;
      if (teamScore > opponentScore) return "W";
      if (teamScore < opponentScore) return "L";
      return "D";
    },
    []
  );
  const adjustScore = (side: "home" | "away", delta: number) => {
    const currentValue = side === "home" ? pick.homeGoals : pick.awayGoals;
    const safeCurrent = typeof currentValue === "number" ? currentValue : 0;
    const nextValue = Math.max(0, Math.min(99, safeCurrent + delta));
    handleScoreInput(side, String(nextValue));
  };
  const formatPoints = (value: number | null) => {
    if (value === null || value === undefined) {
      return "0";
    }
    return value.toString();
  };

  // Not revealed state (Interactive look)
  if (!isRevealed && hoursUntilReveal !== null) {
    return (
      <div className="bg-white rounded-2xl px-4 py-4 flex flex-col items-center justify-center gap-2 w-full h-full border border-transparent" style={{ boxShadow: "0px 2px 8px 0px rgba(0,0,0,0.08)" }}>
        <div className="flex items-center justify-center gap-1">
          {match.competitionFeed && competitionName && (
            <div className="flex gap-1 items-center">
              <img
                src={`https://betcenter-gaming.s3.eu-west-1.amazonaws.com/flags/${match.competitionFeed}.png`}
                alt={competitionName}
                className="w-3 h-3 object-contain"
              />
              <p className="font-normal text-[12px] text-[var(--bet-text)] text-center whitespace-pre">{competitionName}</p>
            </div>
          )}
          {!match.competitionFeed && (
            <p className="font-normal text-[12px] text-[var(--bet-text)] text-center whitespace-pre">{formattedDate}</p>
          )}
        </div>

        <div className="box-border flex gap-2 items-center justify-center px-2 w-full">
        <div className="flex flex-col gap-1 items-center w-[74px]">
          <div className="h-[48px] relative w-[39px]">
            <Image
              src={`/assets/home.png`}
              alt="Home Team"
              width={48}
              height={48}
              className="absolute inset-0 max-w-none object-center object-cover pointer-events-none size-full"
            />
          </div>
        </div>

          <div className="flex-1 flex gap-3 items-center justify-center min-w-0">
            <div className="bg-neutral-100 box-border content-stretch flex flex-col gap-[10px] items-center justify-center px-[9px] py-[6px] relative rounded-[8px] shrink-0 size-[36px]">
              <div className="relative shrink-0 size-[24px]">
                <div className="absolute inset-0 grid place-items-center">
                  <svg viewBox="0 0 24 24" width="16" height="16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="9" stroke="#1f2544" strokeWidth="2" />
                    <path d="M12 8V12L15 15" stroke="#1f2544" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-1 items-center w-[74px]">
          <div className="h-[48px] relative w-[39px]">
            <Image
              src={`/assets/away.png`}
              alt="Away Team"
              width={48}
              height={48}
              className="absolute inset-0 max-w-none object-center object-cover pointer-events-none size-full"
            />
          </div>
        </div>
        </div>

      </div>
    );
  }

  const renderScoreInput = (side: "home" | "away") => {
    const value = side === "home" ? pick.homeGoals : pick.awayGoals;
    const showPlus = value === null || value === undefined;
    const isUpcomingMatch = !isDeadlinePassed && !isResultFinal;
    const isInputInteractive = isUpcomingMatch && !disabled;
    const readOnly = !isInputInteractive;
    const inputRef = side === "home" ? homeInputRef : awayInputRef;
    return (
      <div className="flex flex-col items-center gap-1">
        <div
          onMouseEnter={() => {
            if (onAuthRequired && isInputInteractive) setHoveredScoreInput(side);
          }}
          onMouseLeave={() => {
            if (hoveredScoreInput === side) setHoveredScoreInput(null);
          }}
          onFocusCapture={() => {
            if (onAuthRequired && isInputInteractive) setHoveredScoreInput(side);
          }}
          onBlurCapture={() => {
            if (hoveredScoreInput === side) setHoveredScoreInput(null);
          }}
          className={`relative rounded-[8px] size-[40px] flex items-center justify-center ${
            showPlus ? "bg-[var(--white,white)]" : "bg-[var(--light,#f0f5f9)]"
          } ${
            showPlus
              ? "border border-[var(--rtbf-outline,#d4d4d4)]"
              : "border-2 border-[var(--rtbf-orange,#fdc300)]"
          } focus-within:border-2 focus-within:border-[var(--rtbf-blue,#0076A9)] ${readOnly ? "cursor-not-allowed pointer-events-none" : "cursor-text"}`}
        >
          <input
            ref={inputRef}
            type="text"
            value={showPlus ? "" : value ?? ""}
            onChange={(event) => {
              const raw = event.target.value;
              // Strict single-digit (0-9): keep only the LAST numeric character typed.
              // Combined with no maxLength on the input, this gives us type-to-replace:
              // typing "5" with caret at end of "3" momentarily produces "35", which we
              // immediately truncate back to "5". Also handles paste / fast double-strokes.
              const digitsOnly = raw.replace(/\D/g, "");
              const lastDigit = digitsOnly.slice(-1);
              const nextValue = raw === "" ? "" : lastDigit;
              handleScoreInput(side, nextValue);

              if (nextValue === "") {
                // Backspace / Delete just cleared the digit. The pick transitions
                // from complete -> incomplete, which flips `hasExactScorePick`,
                // unmounts the adjacent save-indicator span, and toggles this
                // wrapper between filled (orange) and empty (grey) styles. That
                // commit-time churn briefly drops focus on some browsers, leaving
                // the user staring at the "+" placeholder with no caret — they
                // can't immediately retype. Defensively refocus this input on the
                // next frame so the caret returns and the user can keep editing
                // their prediction. This is part of the ADR-0001 focus contract.
                requestAnimationFrame(() => {
                  inputRef.current?.focus();
                });
                return;
              }

              // Within-card auto-advance: jump to the OTHER input only when it
              // has no value. If the user is correcting a saved card (the other
              // side already has a digit) we keep focus on the just-edited input
              // so they can keep reviewing. The cross-card jump (fires whenever
              // a keystroke completes the card, from either side) lives in the
              // parent's `handlePickChange` so it can resolve the next match's
              // home input — see ADR-0001.
              if (side === "home" && pick.awayGoals == null) {
                requestAnimationFrame(() => {
                  awayInputRef.current?.focus();
                });
              }
              if (side === "away" && pick.homeGoals == null) {
                requestAnimationFrame(() => {
                  homeInputRef.current?.focus();
                });
              }
            }}
            onFocus={(event) => {
              // Caret at end (instead of select-all) so the blinking "|" is always
              // visible — matches the spec's "you should always see the typing cursor"
              // requirement. Type-to-replace still works via the slice(-1) above.
              const input = event.currentTarget;
              const length = input.value.length;
              requestAnimationFrame(() => {
                try {
                  input.setSelectionRange(length, length);
                } catch {
                  // Some browsers reject setSelectionRange on certain input types;
                  // we use type="text" so this branch is purely defensive.
                }
              });
            }}
            readOnly={readOnly}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="off"
            autoCorrect="off"
            spellCheck={false}
            enterKeyHint={side === "home" ? "next" : "done"}
            placeholder="+"
            data-score-input-interactive={isInputInteractive ? "true" : "false"}
            data-match-home-input={side === "home" ? match.id : undefined}
            data-match-away-input={side === "away" ? match.id : undefined}
            style={{ caretColor: "#001425" }}
            className={`relative z-[1] size-full rounded-[8px] bg-transparent text-center font-semibold leading-normal text-[var(--rtbf-dark,#001425)] outline-none placeholder:font-normal placeholder:text-[var(--rtbf-dark,#001425)] focus:placeholder-transparent ${useDesktopCardLayout ? "text-[32px]" : "text-[24px]"}`}
          />
        </div>
      </div>
    );
  };

  const renderMissedPredictionButton = () => {
    return (
      <div className="border border-[var(--rtbf-outline,#d4d4d4)] bg-[var(--light,#f0f5f9)] rounded-[8px] size-[40px] flex items-center justify-center">
        <span className="text-[32px] leading-none font-semibold text-[var(--rtbf-outline,#d4d4d4)]">-</span>
      </div>
    );
  };
  const renderBoosterSection = () => {
    const hasFinalScore =
      isResultFinal &&
      typeof match.homeScore === "number" &&
      typeof match.awayScore === "number";
    const predictedGoalDifference =
      hasExactScorePick && typeof pick.homeGoals === "number" && typeof pick.awayGoals === "number"
        ? Math.abs(pick.homeGoals - pick.awayGoals)
        : null;
    const actualGoalDifference = hasFinalScore
      ? Math.abs(match.homeScore! - match.awayScore!)
      : null;
    const outcomePoints = hasFinalScore && isPickCorrect ? 3 : 0;
    const homeGoalPoints =
      hasFinalScore && hasExactScorePick && pick.homeGoals === match.homeScore ? 1 : 0;
    const awayGoalPoints =
      hasFinalScore && hasExactScorePick && pick.awayGoals === match.awayScore ? 1 : 0;
    const goalDiffPoints =
      hasFinalScore &&
      predictedGoalDifference !== null &&
      actualGoalDifference !== null &&
      predictedGoalDifference === actualGoalDifference
        ? 1
        : 0;
    const totalPoints = outcomePoints + homeGoalPoints + awayGoalPoints + goalDiffPoints;

    const detailsPanel = isDetailsOpen ? (
      <div className="bg-[var(--rtbf-blue,#0076A9)] px-4 py-5 text-white">
        {hasFinalScore ? (
          <div className="grid grid-cols-[1fr_auto] items-center gap-y-2 gap-x-4 text-[11px] sm:text-[12px] leading-[1.2]">
            <p />
            <p className="text-right font-medium">{t("matchCard.detailsPoints")}</p>

            <p className="font-normal">{t("matchCard.detailsCorrectOutcome")}</p>
            <p className="text-right">+{outcomePoints}</p>

            <p className="font-normal">{t("matchCard.detailsHomeGoals")}</p>
            <p className="text-right">+{homeGoalPoints}</p>

            <p className="font-normal">{t("matchCard.detailsAwayGoals")}</p>
            <p className="text-right">+{awayGoalPoints}</p>

            <p className="font-normal">{t("matchCard.detailsGoalDifference")}</p>
            <p className="text-right">+{goalDiffPoints}</p>
          </div>
        ) : (
          <p className="text-sm text-center">{t("matchCard.detailsAvailableAfterFinalWhistle")}</p>
        )}
      </div>
    ) : null;

    if (isDeadlinePassed) {
      if (isPastNoPrediction) {
        return (
          <div className="h-10 px-4 flex items-center justify-center">
            <p className="text-[12px] md:text-[14px] font-normal leading-none text-white">Aucun pronostic effectué</p>
          </div>
        );
      }
      const statusBaseClasses =
        "flex gap-2 items-center justify-between px-4 py-2 w-full h-10";
      if (!isFinalState || actualWinnerId === null) {
        return (
          <>
            <button
              type="button"
              onClick={() => setIsDetailsOpen((prev) => !prev)}
              className={`${statusBaseClasses} relative bg-[var(--rtbf-blue-dark,#003E63)]`}
            >
              <div className={`text-[14px] font-semibold text-[var(--rtbf-orange,#fdc300)] ${useDesktopCardLayout ? "hidden" : "block"}`}>
                {`0 pts`}
              </div>
              <div className={`${useDesktopCardLayout ? "block" : "hidden"} w-[64px]`} />
              <div className={`${useDesktopCardLayout ? "block" : "hidden"} absolute left-1/2 -translate-x-1/2 text-[14px] font-semibold text-[var(--rtbf-orange,#fdc300)]`}>
                {`Total des points: 0 pts`}
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[14px] font-medium text-white">{t("matchCard.details")}</span>
                <img src={figmaChevronIcon} alt="" className={`size-3 transition-transform ${isDetailsOpen ? "-rotate-90" : "rotate-90"}`} />
              </div>
            </button>
            {detailsPanel}
          </>
        );
      }
      if (isPickCorrect) {
        return (
          <>
            <button
              type="button"
              onClick={() => setIsDetailsOpen((prev) => !prev)}
              className={`${statusBaseClasses} relative bg-[var(--rtbf-blue-dark,#003E63)]`}
            >
              <div className={`text-[14px] font-semibold text-[var(--rtbf-orange,#fdc300)] ${useDesktopCardLayout ? "hidden" : "block"}`}>
                {`${totalPoints} pts`}
              </div>
              <div className={`${useDesktopCardLayout ? "block" : "hidden"} w-[64px]`} />
              <div className={`${useDesktopCardLayout ? "block" : "hidden"} absolute left-1/2 -translate-x-1/2 text-[14px] font-semibold text-[var(--rtbf-orange,#fdc300)]`}>
                {`Total des points: ${totalPoints} pts`}
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[14px] font-medium text-white">{t("matchCard.details")}</span>
                <img src={figmaChevronIcon} alt="" className={`size-3 transition-transform ${isDetailsOpen ? "-rotate-90" : "rotate-90"}`} />
              </div>
            </button>
            {detailsPanel}
          </>
        );
      }
      if (isPickWrong) {
        return (
          <>
            <button
              type="button"
              onClick={() => setIsDetailsOpen((prev) => !prev)}
              className={`${statusBaseClasses} relative bg-[var(--rtbf-blue-dark,#003E63)]`}
            >
              <div className={`text-[14px] font-semibold text-[var(--rtbf-orange,#fdc300)] ${useDesktopCardLayout ? "hidden" : "block"}`}>
                {`${totalPoints} pts`}
              </div>
              <div className={`${useDesktopCardLayout ? "block" : "hidden"} w-[64px]`} />
              <div className={`${useDesktopCardLayout ? "block" : "hidden"} absolute left-1/2 -translate-x-1/2 text-[14px] font-semibold text-[var(--rtbf-orange,#fdc300)]`}>
                {`Total des points: ${totalPoints} pts`}
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-[14px] font-medium text-white">{t("matchCard.details")}</span>
                <img src={figmaChevronIcon} alt="" className={`size-3 transition-transform ${isDetailsOpen ? "-rotate-90" : "rotate-90"}`} />
              </div>
            </button>
            {detailsPanel}
          </>
        );
      }
      return (
        <>
          <button
            type="button"
            onClick={() => setIsDetailsOpen((prev) => !prev)}
            className={`${statusBaseClasses} relative bg-[var(--rtbf-blue-dark,#003E63)]`}
          >
            <div className={`text-[14px] font-semibold text-[var(--rtbf-orange,#fdc300)] ${useDesktopCardLayout ? "hidden" : "block"}`}>
              {`0 pts`}
            </div>
            <div className={`${useDesktopCardLayout ? "block" : "hidden"} w-[64px]`} />
            <div className={`${useDesktopCardLayout ? "block" : "hidden"} absolute left-1/2 -translate-x-1/2 text-[14px] font-semibold text-[var(--rtbf-orange,#fdc300)]`}>
              {`Total des points: 0 pts`}
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-[14px] font-medium text-white">{t("matchCard.details")}</span>
              <img src={figmaChevronIcon} alt="" className={`size-3 transition-transform ${isDetailsOpen ? "-rotate-90" : "rotate-90"}`} />
            </div>
          </button>
          {detailsPanel}
        </>
      );
    }

    if (!hasExactScorePick) {
      return null;
    }

    return (
      <>
        <button
          className="relative flex gap-[10px] items-center justify-between px-4 py-[8px] w-full h-10 bg-[var(--rtbf-blue-dark,#003E63)] transition-colors"
          onClick={() => setIsDetailsOpen((prev) => !prev)}
          disabled={isButtonDisabled}
        >
          <div className={`text-[14px] font-semibold text-[var(--rtbf-orange,#fdc300)] ${useDesktopCardLayout ? "hidden" : "block"}`}>
            {`${totalPoints} pts`}
          </div>
          <div className={`${useDesktopCardLayout ? "block" : "hidden"} w-[64px]`} />
          <div className={`${useDesktopCardLayout ? "block" : "hidden"} absolute left-1/2 -translate-x-1/2 text-[14px] font-semibold text-[var(--rtbf-orange,#fdc300)]`}>
            {`Total des points: ${totalPoints} pts`}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[14px] font-medium text-white">
              {t("matchCard.details")}
            </span>
            <img src={figmaChevronIcon} alt="" className={`size-3 transition-transform ${isDetailsOpen ? "-rotate-90" : "rotate-90"}`} />
          </div>
        </button>
        {detailsPanel}
      </>
    );
  };

  return (
    <MatchCardContainer
      isFavorite={isFavorite}
      yourTeamLabel={t("matchCard.yourTeamYourCall")}
    >
      <div ref={cardRef} className={`relative bg-[var(--rtbf-blue-dark,#003E63)] rounded-t-[8px] px-4 py-4 flex flex-col flex-1 ${useDesktopCardLayout ? "gap-1" : "gap-2"}`}>
        <button
          type="button"
          onClick={() => setIsInfoOpen(true)}
          className="absolute right-2 top-2 inline-flex size-6 items-center justify-center rounded-full text-[#C4D1EA] hover:bg-white/10"
          aria-label={t("matchCard.showRecentMatches")}
        >
          <Info className="size-5" />
        </button>
        <MatchCardHeader
          dateText={formattedDate}
          showFinalScore={showFinalScore}
          scoreText={scoreText}
          useDesktopCardLayout={useDesktopCardLayout}
          isFinalState={isFinalState}
          showLiveScore={showLiveScore}
          isDeadlinePassed={isDeadlinePassed}
        />
        <div className={`box-border flex justify-center px-0 w-full ${useDesktopCardLayout ? "gap-4 items-center" : "gap-0 items-start"}`}>
          <div className={`flex shrink-0 items-center ${useDesktopCardLayout ? "w-auto flex-1 min-w-0 flex-row justify-end gap-2" : "w-[96px] flex-col gap-2"}`}>
            <div className={`flex flex-col items-center gap-2 ${useDesktopCardLayout ? "contents" : ""}`}>
              <div className={`flex items-center ${useDesktopCardLayout ? "h-[40px] order-2 shrink-0" : "h-[32px]"}`}>
                <img
                  src={
                    imgError.home
                      ? TEAM_FLAG_PLACEHOLDER
                      : getTeamFlagSrc(String(match.home.id))
                  }
                  alt={translateTeamName(match.home.name || match.home.short)}
                  className={`w-auto object-contain ${useDesktopCardLayout ? "max-h-[32px]" : "max-h-[24px]"}`}
                  onError={() => setImgError(prev => ({ ...prev, home: true }))}
                />
              </div>
              <p className={`mx-auto min-h-[30px] w-full text-center text-[13px] font-semibold leading-[1.15] text-white line-clamp-2 break-normal ${useDesktopCardLayout ? "hidden" : "block"}`}>
                {translateTeamName(match.home.name || match.home.short)}
              </p>
              <p className={`text-center text-[16px] font-semibold leading-[1.15] text-white whitespace-normal ${useDesktopCardLayout ? "block mx-0 max-w-none order-1 text-right" : "hidden"}`}>
                {translateTeamName(match.home.name || match.home.short)}
              </p>
          </div>
        </div>

          <div ref={scoreColumnRef} className={`relative flex w-[96px] shrink-0 justify-center gap-2 ${useDesktopCardLayout ? "items-center w-[116px]" : "items-start"}`}>
            {isPastNoPrediction ? (
              <>
                {renderMissedPredictionButton()}
                {renderMissedPredictionButton()}
              </>
            ) : (
              <>
                {renderScoreInput("home")}
                {renderScoreInput("away")}
              </>
            )}
            {!isDeadlinePassed && !isResultFinal && hasExactScorePick && saveStatus !== "idle" && (
              <span
                role="status"
                aria-live="polite"
                aria-label={saveStatus === "saved" ? t("matchCard.saved") : t("matchCard.saving")}
                title={saveStatus === "saved" ? t("matchCard.saved") : t("matchCard.saving")}
                className={`pointer-events-none absolute left-1/2 top-[20px] z-[2] inline-flex size-[18px] -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full shadow-[0_1px_2px_rgba(0,0,0,0.25)] ${
                  saveStatus === "saved" ? "bg-[#38B839]" : "bg-[#FDC300]"
                }`}
              >
                <Check className="size-3 text-white" strokeWidth={3} />
              </span>
            )}
          </div>

          <div className={`flex w-[96px] shrink-0 items-center ${useDesktopCardLayout ? "w-auto flex-1 min-w-0 flex-row justify-start gap-2" : "flex-col gap-2"}`}>
            <div className={`flex flex-col items-center gap-2 ${useDesktopCardLayout ? "contents" : ""}`}>
              <div className={`flex items-center ${useDesktopCardLayout ? "h-[40px] shrink-0" : "h-[32px]"}`}>
                <img
                  src={
                    imgError.away
                      ? TEAM_FLAG_PLACEHOLDER
                      : getTeamFlagSrc(String(match.away.id))
                  }
                  alt={translateTeamName(match.away.name || match.away.short)}
                  className={`w-auto object-contain ${useDesktopCardLayout ? "max-h-[32px]" : "max-h-[24px]"}`}
                  onError={() => setImgError(prev => ({ ...prev, away: true }))}
                />
              </div>
              <p className={`mx-auto min-h-[30px] w-full text-center text-[13px] font-semibold leading-[1.15] text-white line-clamp-2 break-normal ${useDesktopCardLayout ? "hidden" : "block"}`}>
                {translateTeamName(match.away.name || match.away.short)}
              </p>
              <p className={`text-center text-[16px] font-semibold leading-[1.15] text-white whitespace-normal ${useDesktopCardLayout ? "block mx-0 max-w-none text-left" : "hidden"}`}>
                {translateTeamName(match.away.name || match.away.short)}
              </p>
          </div>
        </div>
        </div>
      </div>

      {isFinalState ? (
        <div className="bg-[var(--rtbf-blue-dark,#003E63)] border-t border-[var(--rtbf-blue,#0076A9)] rounded-b-[8px] mt-auto">{renderBoosterSection()}</div>
      ) : null}
      {isClient && !!onAuthRequired && hoveredScoreInput !== null && authTooltipPos
        ? createPortal(
            <div
              ref={authTooltipRef}
              className="pointer-events-none fixed z-[60] -translate-x-1/2"
              style={{ left: authTooltipPos.left, top: authTooltipPos.top }}
            >
              <div className="flex flex-col items-center">
                <div
                  className="h-0 w-0 border-l-[6px] border-r-[6px] border-b-[6px] border-l-transparent border-r-transparent border-b-[var(--rtbf-orange,#fdc300)]"
                  style={{ transform: `translateX(${authTooltipPos.arrowOffset}px)` }}
                />
                <div className="max-w-[calc(100vw-16px)] rounded-full bg-[var(--rtbf-orange,#fdc300)] px-3 py-1 text-center text-sm font-[700] text-[var(--rtbf-blue-dark,#003e63)] whitespace-nowrap shadow-[0px_4px_10px_rgba(0,0,0,0.20)]">
                  {t("auth.signIn")}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
      {isClient && isInfoOpen
        ? createPortal(
            <div
              className="fixed inset-0 z-50 grid place-items-center bg-black/80 px-3"
              onMouseDown={(event) => {
                if (event.target === event.currentTarget) {
                  setIsInfoOpen(false);
                }
              }}
            >
              <div
                role="dialog"
                aria-modal="true"
                aria-label={t("matchCard.recentMatchesTitle")}
                className="relative w-full max-w-[760px] rounded-2xl border-none bg-[var(--rtbf-blue-dark,#003E63)] p-4 text-white sm:p-5"
                onMouseDown={(event) => event.stopPropagation()}
              >
                <button
                  type="button"
                  onClick={() => setIsInfoOpen(false)}
                  className="absolute right-3 top-3 inline-flex size-8 items-center justify-center rounded-full text-[#C4D1EA] hover:bg-white/10"
                  aria-label={t("common.close")}
                >
                  <X className="size-5" />
                </button>
                <p className="mb-4 text-[20px] font-normal text-white">{t("matchCard.recentMatchesTitle")}</p>
                <div className="mb-4 border-b border-white/15">
                  <div className="grid grid-cols-2">
                    <button
                      type="button"
                      onClick={() => setActiveInfoTab("home")}
                      className="flex w-full min-w-0 items-center gap-2 pb-3"
                    >
                      <img
                        src={
                          imgError.home
                            ? TEAM_FLAG_PLACEHOLDER
                            : getTeamFlagSrc(String(match.home.id))
                        }
                        alt={translateTeamName(match.home.name || match.home.short)}
                        className="size-7 shrink-0 object-contain"
                      />
                      <span
                        className={`min-w-0 truncate text-[16px] font-semibold ${
                          activeInfoTab === "home" ? "text-white" : "text-[#C4D1EA]"
                        }`}
                      >
                        {translateTeamName(match.home.name || match.home.short)}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setActiveInfoTab("away")}
                      className="flex w-full min-w-0 items-center justify-end gap-2 pb-3"
                    >
                      <img
                        src={
                          imgError.away
                            ? TEAM_FLAG_PLACEHOLDER
                            : getTeamFlagSrc(String(match.away.id))
                        }
                        alt={translateTeamName(match.away.name || match.away.short)}
                        className="size-7 shrink-0 object-contain"
                      />
                      <span
                        className={`min-w-0 truncate text-[16px] font-semibold ${
                          activeInfoTab === "away" ? "text-white" : "text-[#C4D1EA]"
                        }`}
                      >
                        {translateTeamName(match.away.name || match.away.short)}
                      </span>
                    </button>
                  </div>
                  <div className="relative h-[2px] w-full">
                    <div
                      className={`absolute top-0 h-[2px] w-1/2 rounded bg-[var(--rtbf-cyan,#1DD5F0)] ${
                        activeInfoTab === "home" ? "left-0" : "left-1/2"
                      }`}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  {isLoadingRecentMatches ? (
                    <div className="flex justify-center py-6">
                      <Spinner size={20} className="text-white" />
                    </div>
                  ) : null}
                  {!isLoadingRecentMatches && (["home", "away"] as const).map((tabKey) => {
                    const activeTeamId =
                      tabKey === "home" ? String(match.home.id) : String(match.away.id);
                    const tabMatches = recentByTeam[tabKey] || [];
                    return (
                      <div
                        key={tabKey}
                        className={activeInfoTab === tabKey ? "block space-y-4" : "hidden space-y-4"}
                      >
                        {tabMatches.map((recentMatch) => {
                          const resultBadge = getTeamResultBadge(activeTeamId, recentMatch);
                          const badgeColor =
                            resultBadge === "W"
                              ? "bg-[#38B839]"
                              : resultBadge === "L"
                                ? "bg-[#FF4D67]"
                                : "bg-[#6E81B2]";
                          const homeName = recentMatch.home?.name || recentMatch.home?.short || "Home";
                          const awayName = recentMatch.away?.name || recentMatch.away?.short || "Away";
                          const competition =
                            (recentMatch.competitionFeed &&
                              COMPETITION_NAMES[recentMatch.competitionFeed]) ||
                            recentMatch.competitionFeed ||
                            "";
                          return (
                            <div key={recentMatch.id} className="flex items-start gap-3">
                              <span
                                className={`mt-0.5 inline-flex size-7 shrink-0 items-center justify-center rounded-full text-sm font-semibold text-white ${badgeColor}`}
                              >
                                {resultBadge || "-"}
                              </span>
                              <div className="min-w-0">
                                <p className="truncate text-[15px] font-medium text-white">
                                  {`${homeName} ${recentMatch.homeScore}-${recentMatch.awayScore} ${awayName}`}
                                </p>
                                <p className="text-[13px] text-[#C4D1EA]">
                                  {`${recentMatch.date ? moment(recentMatch.date).format("D MMM") : ""}`}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                        {tabMatches.length === 0 ? (
                          <p className="py-6 text-center text-sm text-[#C4D1EA]">
                            {t("matchCard.noRecentMatches")}
                          </p>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>,
            document.body
          )
        : null}
    </MatchCardContainer>
  );
}

export default React.memo(MatchCard);
